import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ToolFlows.scss';
import { CloseIcon } from '../CloseIcon';
import BoostModal from '../BoostModal';
import ListingPickerList, { formatListingAddress } from './ListingPickerList';
import { SellListing, useSellListings } from '../../../hooks/useSellListings';
import { postBoostRequest } from '../../utils/boost';
import { isUnauthorizedError } from '../../utils/priceEstimation';

type BoostFlowProps = {
  open: boolean;
  onClose: () => void;
  /** Empty-state CTA — caller closes Tools and navigates to /could-sell. */
  onGoToSell: () => void;
  userEmail: string | null;
  /** Tools require auth; when false, confirming opens the login modal. */
  isLoggedIn: boolean;
  setLoginModalVisible: (visible: boolean) => void;
};

/**
 * Tools → Visibility boost flow (JAA-285): pick one of your sell listings,
 * confirm in the existing BoostModal, request the concierge boost.
 * 0 listings → empty state with a could-sell CTA; 1 listing → straight to
 * the modal; 2+ → picker.
 */
const BoostFlow: React.FC<BoostFlowProps> = ({
  open,
  onClose,
  onGoToSell,
  userEmail,
  isLoggedIn,
  setLoginModalVisible,
}) => {
  const { t } = useTranslation();
  const { listings, loading, error, fetched, retry } = useSellListings(open);
  const [selected, setSelected] = useState<SellListing | null>(null);
  const [success, setSuccess] = useState(false);

  // Reset per open so a reopened flow starts clean.
  useEffect(() => {
    if (!open) {
      setSelected(null);
      setSuccess(false);
    }
  }, [open]);

  // Single listing skips the picker. Gated on `fetched` so a reopened flow
  // never auto-selects the previous open's stale listing.
  useEffect(() => {
    if (open && fetched && !loading && !error && listings.length === 1 && !selected && !success) {
      setSelected(listings[0]);
    }
  }, [open, fetched, loading, error, listings, selected, success]);

  if (!open) return null;

  const selectedAddress = selected
    ? [formatListingAddress(selected).line1, formatListingAddress(selected).line2]
        .filter(Boolean)
        .join(', ')
    : '';

  const handleConfirm = async () => {
    if (!selected) return;
    // Tools require auth; a stale session would otherwise 401. Route to login.
    if (!isLoggedIn) {
      onClose();
      setLoginModalVisible(true);
      return;
    }
    try {
      await postBoostRequest({
        userEmail,
        eventId: selected._id,
        buildingId: selected.building_id,
        actionType: 'sell',
        buildingAddress: selectedAddress,
      });
      setSuccess(true);
    } catch (err) {
      if (isUnauthorizedError(err)) {
        onClose();
        setLoginModalVisible(true);
        return;
      }
      throw err;
    }
  };

  return (
    <div
      className="tool-flow"
      role="dialog"
      aria-modal="true"
      aria-label={t('newLandingPage.tools.boost.title')}
    >
      <div className="tool-flow__backdrop" onClick={onClose} />
      <div className="tool-flow__panel">
        <div className="tool-flow__header">
          <h2 className="tool-flow__title">{t('newLandingPage.tools.boost.title')}</h2>
          <button
            type="button"
            className="tool-flow__close"
            aria-label={t('newLandingPage.tools.closeAria')}
            onClick={onClose}
          >
            <CloseIcon size={18} color="#131c20" />
          </button>
        </div>

        {loading && (
          <div className="tool-flow__state">
            <p className="tool-flow__state-text">{t('newLandingPage.toolFlows.loading')}</p>
          </div>
        )}

        {!loading && error && (
          <div className="tool-flow__state">
            <p className="tool-flow__state-text">{t('newLandingPage.toolFlows.error')}</p>
            <button type="button" className="tool-flow__cta" onClick={retry}>
              {t('newLandingPage.toolFlows.retry')}
            </button>
          </div>
        )}

        {!loading && !error && listings.length === 0 && (
          <div className="tool-flow__state">
            <p className="tool-flow__state-text">{t('newLandingPage.toolFlows.empty.text')}</p>
            <button type="button" className="tool-flow__cta" onClick={onGoToSell}>
              {t('newLandingPage.toolFlows.empty.cta')}
            </button>
          </div>
        )}

        {!loading && !error && listings.length > 1 && !success && (
          <>
            <p className="tool-flow__meta">{t('newLandingPage.toolFlows.pickerHint')}</p>
            <ListingPickerList listings={listings} onSelect={setSelected} />
          </>
        )}

        {success && (
          <div className="tool-flow__state">
            <p className="tool-flow__state-text">{t('newLandingPage.toolFlows.boostSuccess')}</p>
            <button type="button" className="tool-flow__cta" onClick={onClose}>
              {t('newLandingPage.toolFlows.done')}
            </button>
          </div>
        )}
      </div>

      <BoostModal
        isOpen={Boolean(selected) && !success}
        onClose={() => setSelected(null)}
        onConfirm={handleConfirm}
        actionType="sell"
        buildingAddress={selectedAddress}
      />
    </div>
  );
};

export default BoostFlow;
